/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Grid, Typography2, VisuallyHidden } from '@coursera/cds-core';
import { ErrorIcon } from '@coursera/cds-icons';

import type { CmlContent } from 'bundles/cml';
import { CMLEditorNaptime, CMLUtils, Tools } from 'bundles/cml';
import CharacterLimit from 'bundles/portfolio-projects/components/CharacterLimit';

import _t from 'i18n!nls/portfolio-projects';

const styles = {
  richTextFieldLabel: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  richTextFieldSupportText: css`
    margin-top: var(--cds-spacing-50);
  `,
  richTextField: css`
    position: relative;
    margin-top: var(--cds-spacing-100);

    > * {
      border-radius: var(--cds-spacing-50);

      &:not(:focus-within):hover {
        background: var(--cds-color-blue-25);
      }
    }
  `,
  richTextFieldWithError: css`
    > *:first-of-type {
      border-color: var(--cds-color-red-700);

      &:hover {
        background: var(--cds-color-red-25);
      }

      > *:first-of-type {
        padding-right: var(--cds-spacing-600);
      }
    }
  `,
  characterLimit: css`
    margin-top: var(--cds-spacing-100);
  `,
  errorIcon: css`
    position: absolute;
    top: 0;
    right: 0;
    padding-top: var(--cds-spacing-150);
    padding-right: var(--cds-spacing-150);

    &:not(:focus-within):hover {
      background: none;
    }
  `,
  errorLabel: css`
    display: flex;
    background-color: var(--cds-color-red-25);
    margin-top: var(--cds-spacing-200);
    padding: var(--cds-spacing-50) var(--cds-spacing-100);
    border-radius: var(--cds-spacing-50);
  `,
};

const fieldTextModes = {
  richText: 'rich-text',
  plainText: 'plain-text',
} as const;

type FieldTextMode = typeof fieldTextModes;

type Props = {
  label: string;
  initialCML?: CmlContent;
  supportText?: string;
  placeholder?: string;
  characterLimit?: number;
  readOnly?: boolean;
  validationStatus?: 'error';
  validationLabel?: string;
  ariaLabel?: string;
  mode?: FieldTextMode[keyof FieldTextMode];
  // ref: https://github.com/webedx-spark/cds/blob/next/packages/cds-core/src/forms/FormControl/FormControl.tsx#L19-L29C16
  renderLabel?: (labelComponent: React.ReactElement) => React.ReactElement;
  onContentChange?: (cml: CmlContent) => void;
};

const RichTextField: React.FC<Props> = ({
  initialCML,
  label,
  supportText,
  placeholder,
  characterLimit,
  readOnly,
  validationStatus,
  validationLabel,
  ariaLabel,
  renderLabel,
  onContentChange,
  mode = fieldTextModes.richText,
}) => {
  const [characterCount, setCharacterCount] = useState(CMLUtils.getLength(initialCML));
  const onChange = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (cmlContent: CmlContent) => {
      // NOTE: The way length is calculated includes counting the "\n"'s added to separate the nodes.
      // So the length doesn't represent the number of characters the user has entered
      setCharacterCount(CMLUtils.getLength(cmlContent));
      onContentChange?.(cmlContent);
    },
    [onContentChange]
  );

  const isLimitReached = characterLimit ? characterLimit < characterCount : false;
  const isInvalid = isLimitReached || validationStatus === 'error';
  const formLabel = (
    <Typography2 component="h3" variant="subtitleMedium" css={[styles.richTextFieldLabel]}>
      {label}
    </Typography2>
  );

  useEffect(() => {
    if (initialCML) {
      onChange(initialCML);
    }
  }, [initialCML, onChange]);

  return (
    <Grid container item xs={12} sm={12}>
      <Grid item xs={12} sm={12}>
        {renderLabel ? renderLabel(formLabel) : formLabel}
      </Grid>
      {supportText ? (
        <Grid item xs={12} sm={12}>
          <Typography2 component="p" variant="bodySecondary" css={[styles.richTextFieldSupportText]}>
            {supportText}
          </Typography2>
        </Grid>
      ) : null}
      {validationLabel && validationStatus ? (
        <Grid item xs={12} sm={12} css={[styles.errorLabel]}>
          <VisuallyHidden>{_t('Error: #{error}', { error: validationLabel })}</VisuallyHidden>
          <Typography2 component="span" variant="subtitleMedium" color="error" aria-hidden={true}>
            {validationLabel}
          </Typography2>
        </Grid>
      ) : null}
      <Grid item xs={12} sm={12}>
        <div css={[styles.richTextField, isInvalid && styles.richTextFieldWithError]}>
          <CMLEditorNaptime
            customTools={
              mode === fieldTextModes.plainText
                ? []
                : [
                    Tools.BOLD,
                    Tools.ITALIC,
                    Tools.UNDERLINE,
                    Tools.LINK,
                    Tools.UNLINK,
                    Tools.SUBSCRIPT,
                    Tools.SUPERSCRIPT,
                    Tools.ORDERED_LIST,
                    Tools.UNORDERED_LIST,
                  ]
            }
            initialCML={initialCML}
            placeholder={placeholder}
            onContentChange={onChange}
            ariaLabel={ariaLabel}
            readOnly={readOnly}
          />
          {isInvalid && (
            <div css={[styles.errorIcon]} data-testid="error-suffix">
              <ErrorIcon size="medium" color="error" />
            </div>
          )}
        </div>
      </Grid>
      {characterLimit !== undefined ? (
        <Grid item xs={12} sm={12}>
          <div css={[styles.characterLimit]}>
            <CharacterLimit
              characterLimit={characterLimit}
              charactersLeft={characterLimit - characterCount}
              css={[styles.characterLimit]}
              isLimitReached={isLimitReached}
            />
          </div>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default RichTextField;
